import * as React from "react";
import { CalendarDays } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DayRange, DayRangeCalendar } from "@/features/meetings/DayRangeCalendar";

function fmt(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/**
 * A compact day-range picker: a themed trigger showing the chosen span, opening
 * the same month-grid range calendar the reschedule flow uses (click the first
 * day, then the last). Value + onChange are {@link DayRange} (YYYY-MM-DD strings).
 */
export function DateRangeField({
  value,
  onChange,
  minDate,
  id,
  hidePast,
}: {
  value: DayRange;
  onChange: (range: DayRange) => void;
  minDate?: Date;
  id?: string;
  /** Open on the current week and hide earlier weeks/months (future-only pickers). */
  hidePast?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const label =
    value.start === value.end ? fmt(value.start) : `${fmt(value.start)} – ${fmt(value.end)}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          className="flex h-10 w-full items-center justify-between gap-2 rounded-md border-2 border-black bg-background px-3 text-sm"
        >
          <span className="truncate">{label}</span>
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto rounded-md border-2 border-black p-2">
        <DayRangeCalendar value={value} onChange={onChange} minDate={minDate} hidePast={hidePast} />
      </PopoverContent>
    </Popover>
  );
}
